import FormContext from "Components/Form/context";
import React, {
  createRef,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { TError } from "Services/Error/error";
import { TOption } from "Services/Input/options";
import * as Styled from "./styles";
import { ISelectTheme } from "./theme";
import { gray } from "./themes";
import { useFilters } from "Contexts/FiltersContext";
interface Props {
  name: string;
  options: any;
  placeholder?: string;
  change?(i: any): void;
  width?: number;
  required?: boolean;
  status?(i: boolean): void;
  defaultValue: any;
  theme?: ISelectTheme;
  label?: string;
  disabled?: boolean;
  newSelect?: boolean;
  hideSearch?: boolean;
  mobileMenu?: boolean;
  floatMenu?: boolean;
}
const Select = forwardRef<HTMLDivElement, Props>(
  (
    {
      name,
      placeholder = "",
      options,
      change,
      defaultValue,
      status = () => {},
      width,
      required = false,
      theme = gray,
      disabled = false,
      label,
      newSelect = false,
      hideSearch,
      mobileMenu,
      floatMenu = false,
    },
    ref
  ) => {
    const [expand, setExpand] = useState(false);
    const [value, setValue] = useState(defaultValue || "");
    const [error, setError] = useState<TError>(
      required
        ? {
            message: "Campo Obrigatório",
            status: false,
          }
        : {
            message: "",
            status: false,
          }
    );
    const valueName = options.find(option => option.value === value);
    const { registerField, setFieldError } = useContext(FormContext);
    const inputRef = useMemo(() => createRef<HTMLInputElement>(), []);
    const wrapperRef = useMemo(() => createRef<HTMLDivElement>(), []);
    const contentRef = useRef<HTMLDivElement>(null);
    const [maxHeight, setMaxHeight] = useState(0);
    const { registerResetFn } = useFilters();

    useEffect(() => {
      registerResetFn(() => {
        setExpand(false);
        setValue(defaultValue);
        change && change(defaultValue);
      });
    }, []);

    useEffect(() => {
      if (inputRef.current && defaultValue) {
        inputRef.current.value = defaultValue;
        setError({ message: "", status: false });
      }
    }, [defaultValue, inputRef]);

    useEffect(() => {
      required && setFieldError && setFieldError(name, error);
    }, [defaultValue, error, name, required, setFieldError]);

    useEffect(() => {
      registerField &&
        inputRef.current &&
        registerField(name, inputRef.current, required, false);
    }, [inputRef, name, registerField, required]);

    const expandClick = (value: any) => {
      required && status && status(true);
      setExpand(!expand);
      setValue(value);
      change && change(value);
    };

    useEffect(() => {
      value === null && status(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    const outsideClick = useCallback(
      (e: MouseEvent) => {
        const el = e.target;
        if (
          el instanceof Node &&
          wrapperRef.current &&
          !wrapperRef.current.contains(el)
        ) {
          setExpand(false);
        }
      },
      [wrapperRef]
    );

    useEffect(() => {
      if (expand) document.addEventListener("mousedown", outsideClick);
      else document.removeEventListener("mousedown", outsideClick);
      return () => {
        document.removeEventListener("mousedown", outsideClick);
      };
    }, [expand, outsideClick]);

    useEffect(() => {
      if (contentRef.current) {
        if (!hideSearch) {
          // Expand: calcula a altura real do conteúdo
          setMaxHeight(contentRef.current.scrollHeight);
        } else {
          // Collapse
          setMaxHeight(0);
        }
      }
    }, [hideSearch]);

    if (newSelect == true) {
      return (
        <Styled.ContainerNew
          id="containerNew"
          hideSearch={hideSearch}
          textAlign={theme.textAlign}
          width={width ?? 120}
          ref={ref}
          maxHeight={maxHeight}
        >
          <div ref={contentRef} id="divRel" style={{ overflowY: "visible" }}>
            {label ? (
              <Styled.SchoolingLabel>{label}</Styled.SchoolingLabel>
            ) : (
              <></>
            )}
            <Styled.SelectedNew
              id="selectedNew"
              hideSearch={hideSearch}
              border={theme.border}
              background={theme.background}
              brightness={theme.brightness}
              onClick={() => !disabled && setExpand(!expand)}
              disabled={disabled}
              expand={expand}
            >
              <Styled.LabelNew
                colorLabel={theme.colorLabel}
                justify={theme.justify}
                mobileMenu={mobileMenu}
              >
                {valueName ? valueName.name : placeholder}
              </Styled.LabelNew>
              <Styled.Expand
                expandColor={theme.expandColor}
                mobileMenu={mobileMenu}
              >
                {<Styled.UnfoldMoreNew />}
              </Styled.Expand>
            </Styled.SelectedNew>
            <Styled.OptionsNew
              id="optionsNew"
              textColor={theme.textColor}
              scroll={theme.scroll}
              expand={expand}
              optionsNumber={options.length}
              floatMenu={floatMenu}
            >
              {options.map((option, key) => (
                <Styled.Option
                  id="option"
                  background={theme.backgroundOption}
                  brightness={theme.brightnessOption}
                  hover={theme.hover}
                  border={theme.borderOption}
                  key={`option-${key}`}
                  onClick={() => expandClick(option.value)}
                  mobileMenu={mobileMenu}
                >
                  {option.name}
                </Styled.Option>
              ))}
            </Styled.OptionsNew>
          </div>
        </Styled.ContainerNew>
      );
    } else {
      return (
        <Styled.Container
          hideSearch={hideSearch}
          textAlign={theme.textAlign}
          width={width ?? 120}
          ref={ref}
        >
          {label ? (
            <Styled.SchoolingLabel>{label}</Styled.SchoolingLabel>
          ) : (
            <></>
          )}
          <input type="hidden" name={name} value={value} ref={inputRef} />
          <Styled.Selected
            border={theme.border}
            background={theme.background}
            brightness={theme.brightness}
            onClick={() => !disabled && setExpand(!expand)}
            disabled={disabled}
            expand={expand}
          >
            <Styled.Label
              colorLabel={theme.colorLabel}
              justify={theme.justify}
              mobileMenu={mobileMenu}
            >
              {valueName ? valueName.name : placeholder}
            </Styled.Label>
            <Styled.Expand
              expandColor={theme.expandColor}
              mobileMenu={mobileMenu}
            >
              {<Styled.UnfoldMore />}
            </Styled.Expand>
          </Styled.Selected>
          <Styled.Options
            textColor={theme.textColor}
            scroll={theme.scroll}
            expand={expand}
            optionsNumber={options.length}
            floatMenu={floatMenu}
          >
            {options.map((option, key) => (
              <Styled.Option
                background={theme.backgroundOption}
                brightness={theme.brightnessOption}
                hover={theme.hover}
                border={theme.borderOption}
                key={`option-${key}`}
                onClick={() => expandClick(option.value)}
                mobileMenu={mobileMenu}
              >
                {option.name}
              </Styled.Option>
            ))}
          </Styled.Options>
        </Styled.Container>
      );
    }
  }
);
export default Select;
